import { Tooltip } from '@mui/material'
import { Box } from '@mui/system'
import { FeatureFlagIdBundleAutomationEmergencyShutoff, FeatureFlagIdDisableAllAutomations, FeatureFlagIdEmergencyNotificationShutoff, FeatureFlagIdSystemManagement, FeatureFlagIdWorkflowAutomationEmergencyShutoff } from 'c1gen/features'
import { SettingsV1SystemManagementServiceGetSystemManagementConfig } from 'c1gen/permissions'
import { C1Icon } from 'components/core/icons/C1Icon'
import { useFeatureChecker, useHasPermission } from 'components/no-ui/capability'
import { TriangleAlert } from 'lucide-react'
import { GetSystemManagementConfigRequest } from 'pbts/c1/api/settings/v1/system_management'
import { FC } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { getSystemManagementConfig } from 'redux_toolkit/system-management'

type SystemManagementWarningState = {
    showWarning: boolean
    disabledSettings: string[]
}

/**
 * Hook to get the system management warning state.
 * Returns whether to show a warning and the list of disabled settings.
 */
export const useSystemManagementWarning = (): SystemManagementWarningState => {
    const intl = useIntl()
    const hasSystemManagementFeature = useFeatureChecker(FeatureFlagIdSystemManagement)
    const hasSystemManagementPermission = useHasPermission(SettingsV1SystemManagementServiceGetSystemManagementConfig)

    const isNotificationsSystemDisabled = useFeatureChecker(FeatureFlagIdEmergencyNotificationShutoff)
    const isBundleAutomationsSystemDisabled = useFeatureChecker(FeatureFlagIdBundleAutomationEmergencyShutoff)
    const isWorkflowAutomationsSystemDisabled = useFeatureChecker(FeatureFlagIdWorkflowAutomationEmergencyShutoff)
    const isAllAutomationsSystemDisabled = useFeatureChecker(FeatureFlagIdDisableAllAutomations)

    const { data: configData } = getSystemManagementConfig.useGetSystemManagementConfig(
        GetSystemManagementConfigRequest.create({}),
        { actionable: !!hasSystemManagementFeature && hasSystemManagementPermission },
    )

    if (!hasSystemManagementFeature || !configData) {
        return { showWarning: false, disabledSettings: [] }
    }

    const disabledSettings: string[] = []

    if (configData.notificationsShutoff || isNotificationsSystemDisabled) {
        disabledSettings.push(intl.formatMessage({
            id: 'system_management_notifications_disabled_short',
            defaultMessage: 'Notifications',
        }))
    }
    if (configData.bundleAutomationsShutoff || isBundleAutomationsSystemDisabled) {
        disabledSettings.push(intl.formatMessage({
            id: 'system_management_bundle_automations_disabled_short',
            defaultMessage: 'Access profile enrollments',
        }))
    }
    if (configData.workflowAutomationsShutoff || isWorkflowAutomationsSystemDisabled) {
        disabledSettings.push(intl.formatMessage({
            id: 'system_management_workflow_automations_disabled_short',
            defaultMessage: 'Automations',
        }))
    }
    if (isAllAutomationsSystemDisabled) {
        disabledSettings.push(intl.formatMessage({
            id: 'system_management_all_automations_disabled_short',
            defaultMessage: 'All automations',
        }))
    }

    return {
        showWarning: disabledSettings.length > 0,
        disabledSettings,
    }
}

/**
 * Component that renders a warning indicator for system management.
 * Used by the old nav system (CollapsingSidebar).
 */
export const SystemManagementWarningIndicator: FC = () => {
    const { showWarning, disabledSettings } = useSystemManagementWarning()

    if (!showWarning) {
        return null
    }

    const tooltipContent = (
        <Box>
            <FormattedMessage
                id="system_management_warning_tooltip_title"
                defaultMessage="System features disabled:"
            />
            <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
                {disabledSettings.map((setting, index) => (
                    <li key={index}>{setting}</li>
                ))}
            </ul>
        </Box>
    )

    return (
        <Tooltip title={tooltipContent} placement="bottom">
            <C1Icon icon={TriangleAlert} sx={{ color: 'warning.main' }} />
        </Tooltip>
    )
}
